import React, { useState, useEffect } from "react";
import { Modal, Tab, Tabs } from "react-bootstrap";
import { useLandingPageGenerator, LandingPageContent } from "../../../../hooks/useLandingPageGenerator";

interface Props {
  show: boolean;
  handleClose: () => void;
  projectId: string;
  projectTitle: string;
  projectDescription: string;
}

const templateStyles = [
  {
    id: "modern",
    label: "Modern",
    description: "Clean, minimal, professional",
    icon: "bi-grid-1x2",
  },
  {
    id: "bold",
    label: "Bold",
    description: "Confident, direct, impactful",
    icon: "bi-lightning-charge",
  },
  {
    id: "classic",
    label: "Classic",
    description: "Trustworthy, established, timeless",
    icon: "bi-building",
  },
];

export const WebsiteGeneratorModal: React.FC<Props> = ({
  show,
  handleClose,
  projectId,
  projectTitle,
  projectDescription,
}) => {
  const [selectedStyle, setSelectedStyle] = useState("modern");
  const [activeTab, setActiveTab] = useState("generate");
  const [copied, setCopied] = useState(false);

  const {
    isGenerating,
    isDeploying,
    error,
    generatedPage,
    deployedUrl,
    generateLandingPage,
    deployLandingPage,
    clearError,
    reset,
  } = useLandingPageGenerator();

  // Reset state when modal opens
  useEffect(() => {
    if (show) {
      reset();
      setActiveTab("generate");
      setCopied(false);
    }
  }, [show, reset]);

  const handleGenerate = async () => {
    const result = await generateLandingPage(projectId, selectedStyle);
    if (result) {
      setActiveTab("preview");
    }
  };

  const handleDeploy = async () => {
    const result = await deployLandingPage(projectId);
    if (result) {
      setActiveTab("deployed");
    }
  };

  const handleCopyUrl = () => {
    if (deployedUrl) {
      navigator.clipboard.writeText(deployedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleOpenUrl = () => {
    if (deployedUrl) {
      window.open(deployedUrl, "_blank");
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="xl"
      centered
      backdrop="static"
    >
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fw-bolder">
          <i className="bi bi-globe me-2 text-primary"></i>
          Website Generator
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-3">
        {error && (
          <div className="alert alert-danger mb-4 d-flex align-items-center">
            <i className="bi bi-exclamation-triangle me-2"></i>
            <span className="flex-grow-1">{error}</span>
            <button
              type="button"
              className="btn-close"
              onClick={clearError}
            ></button>
          </div>
        )}

        <Tabs
          activeKey={activeTab}
          onSelect={(k) => k && setActiveTab(k)}
          className="mb-4"
        >
          {/* Generate Tab */}
          <Tab eventKey="generate" title="1. Generate">
            <div className="py-4">
              {/* Project Info */}
              <div className="mb-5">
                <h5 className="fw-bold mb-3">Project: {projectTitle}</h5>
                <div className="bg-light rounded p-3">
                  <p className="text-gray-700 mb-0 fs-7">
                    {projectDescription ||
                      "No description provided. Add a project description for better results."}
                  </p>
                </div>
              </div>

              {/* Template Style Selection */}
              <div className="mb-5">
                <label className="form-label fw-bold fs-6 mb-3">
                  Choose a template style
                </label>
                <div className="row g-3">
                  {templateStyles.map((style) => (
                    <div key={style.id} className="col-md-4">
                      <label
                        className={`d-flex flex-column align-items-center p-4 rounded cursor-pointer border text-center h-100 ${
                          selectedStyle === style.id
                            ? "border-primary bg-light-primary"
                            : "border-gray-300 bg-hover-light"
                        }`}
                        style={{ transition: "all 0.2s ease" }}
                      >
                        <input
                          type="radio"
                          name="templateStyle"
                          value={style.id}
                          checked={selectedStyle === style.id}
                          onChange={(e) => setSelectedStyle(e.target.value)}
                          className="d-none"
                        />
                        <i
                          className={`bi ${style.icon} fs-1 mb-3 ${
                            selectedStyle === style.id
                              ? "text-primary"
                              : "text-gray-500"
                          }`}
                        ></i>
                        <span className="fw-bold fs-5 mb-1">{style.label}</span>
                        <span className="text-muted fs-7">
                          {style.description}
                        </span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* What will be generated */}
              <div className="bg-light-primary rounded p-4">
                <h6 className="fw-bold text-primary mb-3">
                  <i className="bi bi-info-circle me-2"></i>
                  What will be generated
                </h6>
                <div className="row">
                  <div className="col-md-6">
                    <ul className="list-unstyled mb-0">
                      <li className="mb-2">
                        <i className="bi bi-check2 text-success me-2"></i>
                        Hero section with headline
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-check2 text-success me-2"></i>
                        Problem/Solution section
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-check2 text-success me-2"></i>
                        Features showcase
                      </li>
                    </ul>
                  </div>
                  <div className="col-md-6">
                    <ul className="list-unstyled mb-0">
                      <li className="mb-2">
                        <i className="bi bi-check2 text-success me-2"></i>
                        How it works steps
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-check2 text-success me-2"></i>
                        Pricing section
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-check2 text-success me-2"></i>
                        Email capture form
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </Tab>

          {/* Preview Tab */}
          <Tab
            eventKey="preview"
            title="2. Preview"
            disabled={!generatedPage}
          >
            {generatedPage && (
              <div className="py-4">
                {/* Content Preview */}
                <div className="mb-4">
                  <h5 className="fw-bold mb-3">Generated Content</h5>
                  <ContentPreview content={generatedPage.content} />
                </div>

                {/* HTML Preview */}
                <div className="mb-4">
                  <h5 className="fw-bold mb-3">
                    <i className="bi bi-eye me-2"></i>
                    Page Preview
                  </h5>
                  <div
                    className="border rounded"
                    style={{ height: "500px", overflow: "hidden" }}
                  >
                    <iframe
                      srcDoc={generatedPage.html}
                      style={{
                        border: "none",
                        transform: "scale(0.75)",
                        transformOrigin: "top left",
                        width: "133.33%",
                        height: "133.33%",
                      }}
                      title="Landing Page Preview"
                    />
                  </div>
                </div>

                {/* URL Preview */}
                <div className="bg-light rounded p-3">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-link-45deg text-primary me-2"></i>
                    <span className="text-muted">Your page URL will be:</span>
                    <code className="ms-2 text-primary">
                      {generatedPage.url}
                    </code>
                  </div>
                </div>
              </div>
            )}
          </Tab>

          {/* Deployed Tab */}
          <Tab
            eventKey="deployed"
            title="3. Live"
            disabled={!deployedUrl}
          >
            {deployedUrl && (
              <div className="py-5 text-center">
                <div className="mb-4">
                  <div
                    className="d-inline-flex align-items-center justify-content-center bg-success rounded-circle mb-4"
                    style={{ width: "80px", height: "80px" }}
                  >
                    <i className="bi bi-check-lg text-white fs-1"></i>
                  </div>
                  <h3 className="fw-bold mb-2">Your website is live!</h3>
                  <p className="text-muted">
                    Your landing page has been deployed and is now accessible.
                  </p>
                </div>

                <div className="bg-light rounded p-4 mb-4 d-inline-block">
                  <div className="d-flex align-items-center gap-3">
                    <code className="fs-5 text-primary">
                      {deployedUrl}
                    </code>
                    <button
                      className="btn btn-sm btn-light-primary"
                      onClick={handleCopyUrl}
                    >
                      {copied ? (
                        <i className="bi bi-check2"></i>
                      ) : (
                        <i className="bi bi-clipboard"></i>
                      )}
                    </button>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={handleOpenUrl}
                    >
                      <i className="bi bi-box-arrow-up-right me-1"></i>
                      Open
                    </button>
                  </div>
                </div>

                <div className="text-muted fs-7">
                  <p className="mb-1">
                    <i className="bi bi-envelope me-2"></i>
                    Email leads will be captured automatically
                  </p>
                  <p className="mb-0">
                    <i className="bi bi-graph-up me-2"></i>
                    View leads in your project dashboard
                  </p>
                </div>
              </div>
            )}
          </Tab>
        </Tabs>
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        <button
          type="button"
          className="btn btn-light"
          onClick={handleClose}
          disabled={isGenerating || isDeploying}
        >
          {deployedUrl ? "Done" : "Cancel"}
        </button>

        {activeTab === "generate" && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleGenerate}
            disabled={isGenerating || !projectDescription}
          >
            {isGenerating ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Generating...
              </>
            ) : (
              <>
                <i className="bi bi-magic me-2"></i>
                Generate Website
              </>
            )}
          </button>
        )}

        {activeTab === "preview" && generatedPage && (
          <>
            <button
              type="button"
              className="btn btn-light-primary"
              onClick={() => {
                reset();
                setActiveTab("generate");
              }}
              disabled={isDeploying}
            >
              <i className="bi bi-arrow-clockwise me-2"></i>
              Regenerate
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleDeploy}
              disabled={isDeploying}
            >
              {isDeploying ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Deploying...
                </>
              ) : (
                <>
                  <i className="bi bi-cloud-upload me-2"></i>
                  Deploy Website
                </>
              )}
            </button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

// Content preview component
const ContentPreview: React.FC<{ content: LandingPageContent }> = ({
  content,
}) => {
  return (
    <div className="row g-4">
      <div className="col-md-6">
        <div className="border rounded p-3 h-100">
          <h6 className="fw-bold text-primary mb-2">Hero</h6>
          <span className="badge bg-light-primary text-primary mb-2">
            {content.heroBadge}
          </span>
          <p className="fw-bold mb-1">{content.heroHeadline}</p>
          <p className="text-muted fs-7 mb-0">{content.heroSubhead}</p>
        </div>
      </div>
      <div className="col-md-6">
        <div className="border rounded p-3 h-100">
          <h6 className="fw-bold text-primary mb-2">Problem</h6>
          <p className="fw-bold mb-1">{content.problemTitle}</p>
          <ul className="fs-7 mb-0 ps-3">
            {content.problemPoints?.slice(0, 3).map((point, i) => (
              <li key={i} className="text-muted">
                {point}
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="col-md-6">
        <div className="border rounded p-3 h-100">
          <h6 className="fw-bold text-primary mb-2">Solution</h6>
          <p className="fw-bold mb-1">{content.solutionTitle}</p>
          <p className="text-muted fs-7 mb-0">{content.solutionText}</p>
        </div>
      </div>
      <div className="col-md-6">
        <div className="border rounded p-3 h-100">
          <h6 className="fw-bold text-primary mb-2">Features</h6>
          <div className="d-flex flex-wrap gap-2">
            {content.features?.slice(0, 3).map((feature, i) => (
              <span key={i} className="badge bg-light text-dark">
                {feature.icon} {feature.title}
              </span>
            ))}
          </div>
        </div>
      </div>
      <div className="col-md-6">
        <div className="border rounded p-3 h-100">
          <h6 className="fw-bold text-primary mb-2">Pricing</h6>
          <p className="fw-bold fs-4 text-success mb-0">
            {content.pricingAmount}
            {content.pricingPeriod && (
              <span className="fs-7 text-muted ms-1">
                {content.pricingPeriod}
              </span>
            )}
          </p>
        </div>
      </div>
      <div className="col-md-6">
        <div className="border rounded p-3 h-100">
          <h6 className="fw-bold text-primary mb-2">CTA</h6>
          <button className="btn btn-sm btn-primary" disabled>
            {content.ctaText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WebsiteGeneratorModal;
